<template>
  <div class="page-wrapper">
    <div class="container-xl">

      <!-- PAGE HEADER -->
      <div class="page-header d-print-none">
        <div class="row align-items-center">
          <div class="col">
            <div class="page-pretitle">{{ $auth.activeCompany.companyName }}</div>
            <h2 class="page-title">Key Features</h2>
          </div>
        </div>
      </div>

      <!-- LOADING SPINNER -->
      <!-- v-if / v-else: Vue evaluates one branch OR the other, never both.
           This prevents template expressions in the content block from running
           before the API data arrives. -->
      <div class="page-body" v-if="!loaded">
        <div class="d-flex justify-content-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>

      <div class="page-body" v-else>

        <!-- UPSELL BANNER (non-featured companies only) -->
        <!-- Uses the same UpgradeBox component as Profile, Social Media, and other pages
             so the upgrade call-to-action is consistent across the whole app. -->
        <upgrade-box v-if="!$auth.activeCompany.isFeatured" class="mb-4">
          <p>
            Your feature selections are being saved, but they are
            <strong>not currently shown</strong> to potential customers in the directory.
            Upgrade to a featured listing to display your selections on your listing.
          </p>
        </upgrade-box>

        <!-- ERROR STATE -->
        <div class="alert alert-danger" v-if="loadError">
          Something went wrong loading your Key Features. Please refresh the page to try again.
        </div>

        <!-- SAVE ERROR -->
        <div class="alert alert-danger alert-dismissible" v-if="saveError">
          One of your selections could not be saved. It has been reverted. Please try again.
          <button type="button" class="btn-close" @click="saveError = false"></button>
        </div>

        <!-- EMPTY STATE -->
        <!-- Shown when none of the company's categories have key features turned on. -->
        <div class="card" v-if="!loadError && categories.length === 0">
          <div class="card-body text-center py-5">
            <p class="text-muted mb-0">
              There are no key feature categories available for your listings at this time.
            </p>
          </div>
        </div>

        <!-- CATEGORY SECTIONS -->
        <!-- v-for loops over the categories array. Each iteration gets its own card.
             :key="category.categoryId" lets Vue track which card belongs to which category. -->
        <template v-if="!loadError">
        <div
          class="card mb-4"
          v-for="category in categories"
          :key="category.categoryId"
        >
          <div class="card-header">
            <div class="card-title">{{ category.categoryName }}</div>

            <!-- PROGRESS COUNT -->
            <!-- Shown as "3 of 7 features selected". Zero-true companies are nudged to start. -->
            <div class="card-options">
              <span class="text-muted small">
                {{ category.selectedCount }} of {{ category.totalCount }} features selected
              </span>
            </div>
          </div>

          <!-- PROGRESS BAR -->
          <!-- :style binds the width dynamically. The expression runs JS inside the string. -->
          <div class="progress progress-sm card-progress">
            <div
              class="progress-bar bg-success"
              :style="{ width: progressPercent(category) + '%' }"
              role="progressbar"
            ></div>
          </div>

          <div class="card-body">
            <div
              class="mb-3"
              v-for="feature in category.features"
              :key="feature.featureId"
            >
              <!-- TOGGLE SWITCH -->
              <!-- form-switch is Tabler/Bootstrap's toggle style.
                   v-model binds feature.value (a boolean) to the checkbox checked state.
                   When the user flips it, feature.value updates immediately, then @change fires. -->
              <label class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="feature.value"
                  @change="toggleFeature(category, feature)"
                  :disabled="savingIds.includes(feature.featureId)"
                />
                <!-- FEATURE NAME + TOOLTIP ICON -->
                <!-- The title attribute on the svg gives a native browser tooltip.
                     v-if on the icon means it only renders when description exists. -->
                <span class="form-check-label">
                  {{ feature.name }}
                  <span
                    v-if="feature.description"
                    :title="feature.description"
                    style="cursor: help; vertical-align: middle;"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon icon-tabler icon-tabler-info-circle ms-1 text-muted"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <circle cx="12" cy="12" r="9"></circle>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      <polyline points="11 12 12 12 12 16 13 16"></polyline>
                    </svg>
                  </span>
                  <!-- Saving indicator: appears only while this feature's API call is in flight -->
                  <span
                    v-if="savingIds.includes(feature.featureId)"
                    class="spinner-border spinner-border-sm ms-2 text-muted"
                    role="status"
                  ></span>
                </span>
              </label>
            </div>
          </div>
        </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import CategoryKeyFeaturesService from "../../services/CategoryKeyFeaturesService";
import UpgradeBox from "../../components/Upgrade/UpgradeBox.vue";

export default {
  name: "company.key-features",
  components: { UpgradeBox },

  // data() returns the initial reactive state of the component.
  // Vue watches everything returned here. When any of these values change,
  // the parts of the template that use them automatically re-render.
  data() {
    return {
      loaded: false,
      loadError: false,
      saveError: false,
      categories: [],
      savingIds: [],
    };
  },

  methods: {
    async load() {
      const data = await CategoryKeyFeaturesService.getCompanyFeatures();
      this.categories = data.categories ?? [];
    },

    // Called the moment a toggle changes (v-model has already updated feature.value).
    // We optimistically keep the UI state and fire the save in the background.
    async toggleFeature(category, feature) {
      this.savingIds.push(feature.featureId);
      const previousValue = !feature.value;

      try {
        await CategoryKeyFeaturesService.saveSelection(
          category.categoryId,
          feature.featureId,
          feature.value
        );
        category.selectedCount = category.features.filter((f) => f.value).length;
      } catch {
        feature.value = previousValue;
        category.selectedCount = category.features.filter((f) => f.value).length;
        this.saveError = true;
      } finally {
        this.savingIds = this.savingIds.filter((id) => id !== feature.featureId);
      }
    },

    // Returns a 0-100 number for the progress bar width.
    // Called from :style in the template.
    progressPercent(category) {
      if (category.totalCount === 0) return 0;
      return Math.round((category.selectedCount / category.totalCount) * 100);
    },
  },

  // mounted() runs once, after the component is added to the page.
  // It's the right place to load data. We mark loaded = true after the
  // API call completes so the spinner shows while we wait.
  async mounted() {
    try {
      await this.load();
    } catch (e) {
      console.error("KeyFeatures load error:", e);
      this.loadError = true;
    } finally {
      // finally runs whether load() succeeded or threw.
      // We always flip loaded so the spinner clears.
      this.loaded = true;
    }
  },
};
</script>
